import { MED_RECEPTION_TABS } from "@/utils/constants";
import { downloadFile, filterObject } from "@/utils/helpers";
import { useAxios } from "@/utils/hooks/useAxios";
import { useSystem } from "@/utils/hooks/useLocation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "react-toastify";

export const useGetMadinaAllBagsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  params = filterObject(params)

  return useQuery({
    queryKey: ["madina-all-bags", params],
    queryFn: () => axiosProtected.get(`${currentSystem}/nusk-arrival-flights/bags`, { params }),
  });
};
export const useGetMadinaOneBagQuery = () => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();
  const { request_number, bag_id } = useParams();

  return useQuery({
    queryKey: ["madina-one-bag", request_number, bag_id],
    queryFn: () => axiosProtected.get(`${currentSystem}/nusk-arrival-flights/${request_number}/bags/${bag_id}`),
    enabled: !!request_number && !!bag_id,
  });
};

export const useGetMadinaArrivalsQuery = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { currentSystem } = useSystem();

  const [searchParams] = useSearchParams();
  let date = searchParams.get("date") || "today";
  const { date: queryDate, ...restParams } = getParams({ params, date });


  return useQuery({
    queryKey: ["madina-arrivals", restParams, queryDate],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/nusk-arrival-flights${queryDate}`, {
        params: { ...restParams, direction_id: 2 },
      }),
  });
};


export const useMadinaMarkAsArrivedMutation = (params = {}) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  const [searchParams] = useSearchParams();
  let date = searchParams.get("date") || "today";
  const { date: queryDate, ...restParams } = getParams({ params, date });

  return useMutation({
    mutationFn: ({ flight_id, ...restPayload }) => axiosProtected.put(`${currentSystem}/nusk-arrival-flights/${flight_id}/arrive`, restPayload),

    onSuccess: (data) => {
      toast.success(t("MARKED_ARRIVED_SUCCESSFULLY"));
      const response = data.data.data;
      queryClient.setQueryData(["madina-arrivals", restParams, queryDate], (old) => {
        return produce(old, (draft) => {
          draft.data.data = draft.data.data.map((item) => {
            if (item.request_number === response.request_number) {
              return { ...item, arrived_at: response.arrived_at };
            }

            return item;
          });
        });
      })

    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("MARK_ARRIVED_FAILED"));
    },
  });
};
export const useMadinaMarkAsCancelledMutation = (params) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();

  const [searchParams] = useSearchParams();
  let date = searchParams.get("date") || "today";
  const { date: queryDate, ...restParams } = getParams({ params, date });
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flight_id }) => axiosProtected.put(`${currentSystem}/nusk-arrival-flights/${flight_id}/cancel`),

    onSuccess: (data) => {
      toast.success(t("CANCEL_FLIGHT_SUCCESSFULLY"));

      const response = data.data.data;
      queryClient.setQueryData(["madina-arrivals", restParams, queryDate], (old) => {
        return produce(old, (draft) => {
          draft.data.data = draft.data.data.map((item) => {
            if (item.request_number === response.request_number) {
              return { ...item, arrived_at: response.arrived_at };
            }

            return item;
          });
        });
      })
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("CANCEL_FLIGHT_FAILED"));
    },
  });
};
export const useMadinaDeleteBagMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: ({
      request_number,
      bag_id
    }) => axiosProtected.delete(`${currentSystem}/nusk-arrival-flights/${request_number}/bags/${bag_id}`),
    onSuccess: (data) => {
      toast.success(t("BAG_DELETED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["madina-all-bags"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || t("DELETE_BAG_FAILED"));
    },
  });
};

const getParams = ({ params, date }) => {

  date = date.replace(MED_RECEPTION_TABS.all.key, "");
  date = date.replace(
    MED_RECEPTION_TABS.today.key,
    "/" + MED_RECEPTION_TABS.today.key
  );
  return {
    ...filterObject(params),
    date,
  };
};

export const useMadinaPrintBagMutation = () => {
  const { axiosProtected } = useAxios();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "ae" ? "ar" : "en";
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: ({ request_number, id }) => axiosProtected.get(`${currentSystem}/nusk-arrival-flights/${request_number}/bags/${id}/print?locale=${locale || "ar"}`, {
      responseType: "blob",
    }),
    onSuccess: (data, variables) => {
      const filename = t("ONEL_BAG_REPORT", {
        bag_number: variables.bag_number,
        request_number: variables.request_number,
      })
      const blob = new Blob([data.data], { type: "application/pdf" });
      downloadFile(blob, `${filename}.pdf`);
    },
  });
};

export const useMadinaPrintBagListMutation = () => {
  const { axiosProtected } = useAxios();
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const locale = i18n.language === "ae" ? "ar" : "en";
  const { currentSystem } = useSystem();

  return useMutation({
    mutationFn: (params) => axiosProtected.get(`${currentSystem}/nusk-arrival-flights/bags/routing-report`, {
      responseType: "blob",
      params: { ...filterObject(params), locale: locale || "ar" }
    }),
    onSuccess: (data, variables) => {
      const filename = t("BAGS_REPORT") + "-" + new Date().getTime();
      const blob = new Blob([data.data], { type: "application/pdf" });
      downloadFile(blob, `${filename}.pdf`);
    },
  });
};

// ==============================
//  Report CRUD
// ==============================
export const useMadinaCreateReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();


  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => axiosProtected.post(`${currentSystem}/nusk-arrival-flights`, payload),
    onSuccess: () => {
      toast.success(t("REPORT_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["madina-arrivals"] });
    },
  });
};
export const useMadinaUpdateReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => axiosProtected.put(`${currentSystem}/nusk-arrival-flights/${id}`, payload),
    onSuccess: () => {
      toast.success(t("REPORT_UPDATED_SUCCESSFULLY"));
      navigate(`/app/${currentSystem}/reception/${id}/view`);
      queryClient.invalidateQueries({ queryKey: ["madina-one-report", id] });
    },
  });
};
export const useMadinaDeleteReportMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { currentSystem } = useSystem();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => axiosProtected.delete(`${currentSystem}/nusk-arrival-flights/${id}`),
    onSuccess: () => {
      toast.success(t("REPORT_DELETED_SUCCESSFULLY"));
      queryClient.invalidateQueries({ queryKey: ["madina-arrivals"] });
    },
  });
};
export const useMadinaOneReportQuery = ({ enabled = true }) => {
  const { axiosProtected } = useAxios();
  const { id } = useParams();
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["madina-one-report", id],
    queryFn: () => axiosProtected.get(`${currentSystem}/nusk-arrival-flights/${id}`),
    enabled: !!id && enabled,
  });
};
